import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Zap, Award, ShoppingBag, TrendingUp, TrendingDown, Star, Edit } from 'lucide-react';

const AdminEditButton = ({ onEdit, testId }) => {
  if (!onEdit) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      data-testid={testId}
      onClick={onEdit}
      className="h-7 px-2 border-purple-400 text-purple-600 hover:bg-purple-50"
    >
      <Edit className="w-3.5 h-3.5 ml-1" />
      <span className="text-xs">ערוך</span>
    </Button>
  );
};

const PlayerProfile = ({ profile, className = '', onAdminEdit }) => {
  if (!profile) return null;

  const hasPlayingStyle = profile.playing_style || profile.strengths || profile.weaknesses || profile.favorite_surface;
  const hasFavoritePlayers = profile.favorite_player_1 || profile.favorite_player_2 || profile.favorite_player_3;
  const hasEquipment = profile.racket || profile.shoes;

  // לאדמין מציגים תמיד את כל הקטעים (גם אם ריקים) כדי שיוכל לערוך
  const showEmptyForAdmin = !!onAdminEdit;

  if (!hasPlayingStyle && !hasFavoritePlayers && !hasEquipment && !showEmptyForAdmin) {
    return null;
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${className}`}>
      {/* Playing Style */}
      {(hasPlayingStyle || showEmptyForAdmin) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Zap className="w-5 h-5 text-orange-600" />
                סגנון משחק
              </CardTitle>
              <AdminEditButton onEdit={onAdminEdit} testId="edit-playing-style-btn" />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {profile.playing_style && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">🎾</span>
                <div>
                  <span className="text-xs text-gray-500">סגנון:</span>
                  <p className="font-semibold text-gray-800">{profile.playing_style}</p>
                </div>
              </div>
            )}

            {profile.strengths && (
              <div className="p-2 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-xs font-semibold text-green-700">נקודות חזקות</span>
                </div>
                <p className="text-sm text-gray-700">{profile.strengths}</p>
              </div>
            )}

            {profile.weaknesses && (
              <div className="p-2 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingDown className="w-4 h-4 text-orange-600" />
                  <span className="text-xs font-semibold text-orange-700">לשיפור</span>
                </div>
                <p className="text-sm text-gray-700">{profile.weaknesses}</p>
              </div>
            )}

            {profile.favorite_surface && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">⭐</span>
                <div>
                  <span className="text-xs text-gray-500">משטח מועדף:</span>
                  <p className="font-semibold text-gray-800">{profile.favorite_surface}</p>
                </div>
              </div>
            )}

            {!hasPlayingStyle && showEmptyForAdmin && (
              <p className="text-sm text-gray-400 italic">לא הוזנו נתונים. לחץ "ערוך" כדי להוסיף.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Favorite Players */}
      {(hasFavoritePlayers || showEmptyForAdmin) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Star className="w-5 h-5 text-yellow-600" />
                שחקנים מועדפים
              </CardTitle>
              <AdminEditButton onEdit={onAdminEdit} testId="edit-favorite-players-btn" />
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {profile.favorite_player_1 && (
              <div className="flex items-center gap-3 p-2 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg">
                <Badge className="bg-yellow-600 text-white">🥇</Badge>
                <span className="font-semibold text-gray-800">{profile.favorite_player_1}</span>
              </div>
            )}

            {profile.favorite_player_2 && (
              <div className="flex items-center gap-3 p-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                <Badge className="bg-gray-500 text-white">🥈</Badge>
                <span className="font-semibold text-gray-800">{profile.favorite_player_2}</span>
              </div>
            )}

            {profile.favorite_player_3 && (
              <div className="flex items-center gap-3 p-2 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg">
                <Badge className="bg-orange-600 text-white">🥉</Badge>
                <span className="font-semibold text-gray-800">{profile.favorite_player_3}</span>
              </div>
            )}

            {!hasFavoritePlayers && showEmptyForAdmin && (
              <p className="text-sm text-gray-400 italic">לא הוזנו שחקנים מועדפים.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Equipment */}
      {(hasEquipment || showEmptyForAdmin) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShoppingBag className="w-5 h-5 text-blue-600" />
                הציוד שלי
              </CardTitle>
              <AdminEditButton onEdit={onAdminEdit} testId="edit-equipment-btn" />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {profile.racket && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">🎾</span>
                  <span className="text-xs font-semibold text-blue-700">רקטה</span>
                </div>
                <p className="font-semibold text-gray-800">{profile.racket}</p>
              </div>
            )}

            {profile.shoes && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">👟</span>
                  <span className="text-xs font-semibold text-purple-700">נעליים</span>
                </div>
                <p className="font-semibold text-gray-800">{profile.shoes}</p>
              </div>
            )}

            {!hasEquipment && showEmptyForAdmin && (
              <p className="text-sm text-gray-400 italic">לא הוזן ציוד.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PlayerProfile;
